import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { AvailabilityService } from '@/lib/services/availabilityService';
import { CustomerAvailabilityService } from '@/lib/services/customerAvailabilityService';
import { WaitingRoom } from '@/components/video/WaitingRoom';
import { FloatingVideoCall } from '@/components/video/FloatingVideoCall';
import type { CallOutcome } from '@/lib/clinic/callSession';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OTPInput } from '@/components/ui/otp-input';
import { 
  Loader2, 
  AlertCircle, 
  Stethoscope, 
  Clock,
  CheckCircle,
  Users,
  Phone,
  MessageSquare,
  ArrowLeft,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { sendOTP, verifyOTP, resendOTP, sendLoginOTP } from '@/lib/services/otp-verification';
import { sendWhatsAppOTP, verifyWhatsAppOTP } from '@/lib/firebase/auth/whatsapp-phone-auth';
import { useOTPMethod } from '@/hooks/use-otp-method';
import { nameTranslationService } from '@/lib/services/nameTranslationService';
import { signInWithGoogle } from '@/lib/firebase/auth/google';

type PagePhase = 'loading' | 'info' | 'phone' | 'otp' | 'details' | 'queue' | 'call' | 'error' | 'closed';

export function PublicClinicPage() {
  const { coachId, linkId } = useParams<{ coachId: string; linkId?: string }>();
  const navigate = useNavigate();
  const { user, userData, loginWithWhatsAppOTP } = useAuth();
  const { language, t, dir } = useLanguage();
  const { otpMethod } = useOTPMethod();
  
  // Track if this is a customer-specific link (no login required)
  const [isCustomerLink, setIsCustomerLink] = useState(false);
  
  const [phase, setPhase] = useState<PagePhase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [coachData, setCoachData] = useState<any>(null);
  const [clinicStatus, setClinicStatus] = useState<string>('closed');
  const [queueLength, setQueueLength] = useState<number>(0);
  const [roomId, setRoomId] = useState<string>('');
  
  // Customer info states
  const [customerId, setCustomerId] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  
  // OTP states
  const [phoneInput, setPhoneInput] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState<Date | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [whatsappVerificationId, setWhatsappVerificationId] = useState<string | null>(null);
  
  // Registration details states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstNameAr, setFirstNameAr] = useState('');
  const [lastNameAr, setLastNameAr] = useState('');
  const [firstNameEn, setFirstNameEn] = useState('');
  const [lastNameEn, setLastNameEn] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!coachId) {
      setError(language === 'ar' ? 'رابط غير صالح' : 'Invalid link');
      setPhase('error');
      return;
    }

    // Don't reload if already in queue or call phase
    if (phase === 'queue' || phase === 'call') {
      return;
    }

    // Check if this is a customer-specific link (no login required)
    if (linkId) {
      loadCustomerLink();
    } else {
      // Generic clinic link - may require login
      // Save clinic URL to sessionStorage so user returns here after login
      if (!user) {
        const clinicUrl = `/clinic/${coachId}`;
        sessionStorage.setItem('redirectAfterLogin', clinicUrl);
      }
      loadClinicData();
    }
  }, [coachId, linkId, user, userData]);

  // Load customer-specific link data (no login required)
  const loadCustomerLink = async () => {
    try {
      setPhase('loading');
      
      if (!linkId || !coachId) {
        setError(language === 'ar' ? 'رابط غير صالح' : 'Invalid link');
        setPhase('error');
        return;
      }

      // Get customer info from the link
      const customerInfo = await CustomerAvailabilityService.getPublicCustomerInfo(linkId);
      
      if (!customerInfo) {
        setError(language === 'ar' ? 'الرابط غير صالح أو منتهي الصلاحية' : 'Link is invalid or expired');
        setPhase('error');
        return;
      }

      // Load coach data
      const coachDoc = await getDoc(doc(db, 'users', coachId));
      if (!coachDoc.exists()) {
        setError(language === 'ar' ? 'العيادة غير موجودة' : 'Clinic not found');
        setPhase('error');
        return;
      }

      setCoachData(coachDoc.data());

      // Get clinic status
      const session = await AvailabilityService.getCoachAvailability(coachId);
      const status = session?.status || 'closed';
      setClinicStatus(status);

      if (status === 'closed') {
        setPhase('closed');
        return;
      }

      // Set customer info from link (no login needed!)
      setIsCustomerLink(true);
      setCustomerId(customerInfo.customerId);
      setCustomerName(customerInfo.customerName);
      setCustomerPhone(customerInfo.customerPhone || '');

      // Go directly to waiting room
      setPhase('queue');
      
    } catch (error) {
      console.error('Error loading customer link:', error);
      setError(language === 'ar' ? 'فشل في تحميل الرابط' : 'Failed to load link');
      setPhase('error');
    }
  };

  // Subscribe to clinic status changes
  useEffect(() => {
    if (!coachId) return;

    const unsubscribe = AvailabilityService.listenToWaitingRoom(
      coachId,
      (waitingRoom) => {
        if (waitingRoom) {
          setClinicStatus(waitingRoom.coachStatus);
          setQueueLength(waitingRoom.customers?.length || 0);
        }
      }
    );

    return unsubscribe;
  }, [coachId]);

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // OTP expiry timer effect
  useEffect(() => {
    if (otpExpiry) {
      const timer = setInterval(() => {
        const now = new Date();
        if (now >= otpExpiry) {
          setOtpExpiry(null);
          toast.error(language === 'ar' ? 'انتهت صلاحية الرمز' : 'Verification code has expired');
          setPhase('phone');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpExpiry, language]);

  const loadClinicData = async () => {
    try {
      setPhase('loading');
      
      if (!coachId) {
        setError(language === 'ar' ? 'رابط غير صالح' : 'Invalid link');
        setPhase('error');
        return;
      }

      // Load coach data
      const coachDoc = await getDoc(doc(db, 'users', coachId));
      if (!coachDoc.exists()) {
        setError(language === 'ar' ? 'العيادة غير موجودة' : 'Clinic not found');
        setPhase('error');
        return;
      }

      const coach = coachDoc.data();
      setCoachData(coach);

      // Get clinic status
      const session = await AvailabilityService.getCoachAvailability(coachId);
      const status = session?.status || 'closed';
      setClinicStatus(status);

      // Check if clinic is accepting customers
      if (status === 'closed') {
        setPhase('closed');
        return;
      }

      // If user is logged in, auto-join the waiting room
      if (user && userData) {
        const name = language === 'ar' 
          ? `${userData.firstNameAr || userData.firstNameEn} ${userData.lastNameAr || userData.lastNameEn}`
          : `${userData.firstNameEn || userData.firstNameAr} ${userData.lastNameEn || userData.lastNameAr}`;
        
        setCustomerId(user.uid);
        setCustomerPhone(userData.phone || '');
        setCustomerName(name);
        
        // Auto-join the waiting room immediately
        setPhase('queue');
      } else {
        // Not logged in - go directly to phone input
        setPhase('phone');
      }
    } catch (error) {
      console.error('Error loading clinic data:', error);
      setError(language === 'ar' ? 'فشل في تحميل بيانات العيادة' : 'Failed to load clinic data');
      setPhase('error');
    }
  };

  const handleSendOTP = async () => {
    if (!phoneInput || !/^05\d{8}$/.test(phoneInput)) {
      toast.error(language === 'ar' ? 'الرجاء إدخال رقم جوال صحيح (05XXXXXXXX)' : 'Please enter a valid phone (05XXXXXXXX)');
      return;
    }

    try {
      setIsOtpLoading(true);
      const lang = language === 'ar' ? 'ar' : 'en';
      
      // First try to send OTP for new user registration
      const result = await sendOTP(phoneInput, lang);
      
      if (result.success) {
        // New user registration flow
        setIsExistingUser(false);
        setCustomerPhone(phoneInput);
        setPhase('otp');
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000));
        setResendCooldown(60);
        toast.success(language === 'ar'
          ? (otpMethod === 'sms' ? 'تم إرسال رمز التحقق عبر رسالة نصية' : 'تم إرسال رمز التحقق إلى الواتساب')
          : (otpMethod === 'sms' ? 'Verification code sent via SMS' : 'Verification code sent to your WhatsApp'));
      } else if (result.error?.includes('already registered')) {
        // Phone is already registered - use login OTP
        toast.info(language === 'ar' ? 'رقم الجوال مسجل مسبقاً، جاري إرسال رمز الدخول...' : 'Phone number is already registered. Sending login code...');
        
        // Format phone number for WhatsApp OTP login
        const formattedPhone = `+966${phoneInput.substring(1)}`;
        const whatsappResult = await sendWhatsAppOTP(formattedPhone, lang as 'en' | 'ar');
        
        if (whatsappResult.success && whatsappResult.verificationId) {
          setIsExistingUser(true);
          setWhatsappVerificationId(whatsappResult.verificationId);
          setCustomerPhone(phoneInput);
          setPhase('otp');
          setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000));
          setResendCooldown(60);
          toast.success(language === 'ar'
            ? (otpMethod === 'sms' ? 'تم إرسال رمز الدخول عبر رسالة نصية' : 'تم إرسال رمز الدخول إلى الواتساب')
            : (otpMethod === 'sms' ? 'Login code sent via SMS' : 'Login code sent to your WhatsApp'));
        } else {
          toast.error(whatsappResult.error || (language === 'ar' ? 'فشل في إرسال رمز الدخول' : 'Failed to send login code'));
        }
      } else {
        toast.error(result.error || (language === 'ar' ? 'فشل في إرسال رمز التحقق' : 'Failed to send verification code'));
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(language === 'ar' ? 'فشل في إرسال رمز التحقق' : 'Failed to send verification code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpValue.length !== 4) {
      toast.error(language === 'ar' ? 'الرجاء إدخال رمز صحيح من 4 أرقام' : 'Please enter a valid 4-digit code');
      return;
    }

    try {
      setIsOtpLoading(true);
      
      if (isExistingUser && whatsappVerificationId) {
        // Existing user login flow
        const result = await verifyWhatsAppOTP(whatsappVerificationId, otpValue);
        
        if (result.success && result.userData) {
          if (result.authSessionEstablished === false) {
            toast.error(language === 'ar' ? 'تعذّر إنشاء الجلسة. حاول مرة أخرى.' : 'Could not establish a secure session. Please try again.');
            setOtpValue('');
            return;
          }
          // Login the user
          await loginWithWhatsAppOTP(customerPhone);
          
          // Set customer info from existing user data
          setCustomerId(result.userData.id);
          setCustomerName(
            language === 'ar' 
              ? `${result.userData.firstNameAr || result.userData.firstNameEn} ${result.userData.lastNameAr || result.userData.lastNameEn}`
              : `${result.userData.firstNameEn || result.userData.firstNameAr} ${result.userData.lastNameEn || result.userData.lastNameAr}`
          );
          
          toast.success(language === 'ar' ? 'تم تسجيل الدخول بنجاح!' : 'Logged in successfully!');
          
          // Join queue automatically
          await handleJoinQueue(result.userData.id, 
            language === 'ar' 
              ? `${result.userData.firstNameAr || result.userData.firstNameEn} ${result.userData.lastNameAr || result.userData.lastNameEn}`
              : `${result.userData.firstNameEn || result.userData.firstNameAr} ${result.userData.lastNameEn || result.userData.lastNameAr}`,
            customerPhone
          );
        } else {
          toast.error(result.error || (language === 'ar' ? 'رمز التحقق غير صحيح' : 'Invalid verification code'));
          setOtpValue('');
        }
      } else {
        // New user registration flow
        const result = await verifyOTP(customerPhone, otpValue);
        
        if (result.success) {
          setPhase('details');
          toast.success(language === 'ar' ? 'تم التحقق من رقم الجوال!' : 'Phone number verified!');
        } else {
          toast.error(result.error || (language === 'ar' ? 'رمز التحقق غير صحيح' : 'Invalid verification code'));
          setOtpValue('');
        }
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(language === 'ar' ? 'فشل في التحقق من الرمز' : 'Failed to verify code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResendLoading(true);
      const lang = language === 'ar' ? 'ar' : 'en';
      const result = await resendOTP(customerPhone, lang);
      
      if (result.success) {
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000));
        setResendCooldown(60);
        setOtpValue('');
        toast.success(language === 'ar' ? 'تم إرسال رمز جديد' : 'New code sent');
      } else {
        toast.error(result.error || (language === 'ar' ? 'فشل في إرسال الرمز' : 'Failed to resend code'));
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error(language === 'ar' ? 'فشل في إرسال الرمز' : 'Failed to resend code');
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      
      const { user: googleUser, userData: googleUserData } = await signInWithGoogle();
      
      if (googleUserData) {
        // Set customer info from Google user data
        const finalCustomerId = googleUserData.id;
        const finalCustomerName = language === 'ar' 
          ? `${googleUserData.firstNameAr || googleUserData.firstNameEn} ${googleUserData.lastNameAr || googleUserData.lastNameEn}`
          : `${googleUserData.firstNameEn || googleUserData.firstNameAr} ${googleUserData.lastNameEn || googleUserData.lastNameAr}`;
        const finalCustomerPhone = googleUserData.phone || '';
        
        setCustomerId(finalCustomerId);
        setCustomerName(finalCustomerName);
        setCustomerPhone(finalCustomerPhone);
        
        toast.success(language === 'ar' ? 'تم تسجيل الدخول بنجاح!' : 'Signed in successfully!');
        
        // Automatically join the queue
        await handleJoinQueue(finalCustomerId, finalCustomerName, finalCustomerPhone);
      }
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      
      // Handle specific error messages
      if (error.message?.includes('cancelled') || error.message?.includes('closed')) {
        // User cancelled, don't show error
        return;
      }
      
      if (error.message?.startsWith('auth.')) {
        toast.error(t(error.message) || (language === 'ar' ? 'فشل تسجيل الدخول بـ Google' : 'Google sign-in failed'));
      } else {
        toast.error(error.message || (language === 'ar' ? 'فشل تسجيل الدخول بـ Google' : 'Google sign-in failed'));
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleNameBlur = async () => {
    if (!firstName || !lastName) return;
    
    try {
      setIsTranslating(true);
      
      // Check if name contains Arabic characters
      const containsArabic = /[\u0600-\u06FF]/.test(firstName) || /[\u0600-\u06FF]/.test(lastName);
      
      if (containsArabic) {
        // User entered Arabic, translate to English
        setFirstNameAr(firstName);
        setLastNameAr(lastName);
        const translated = await nameTranslationService.translateToEnglish(firstName, lastName);
        setFirstNameEn(translated.firstNameEn);
        setLastNameEn(translated.lastNameEn);
      } else {
        // User entered English, translate to Arabic
        setFirstNameEn(firstName);
        setLastNameEn(lastName);
        const translated = await nameTranslationService.translateToArabic(firstName, lastName);
        setFirstNameAr(translated.firstNameAr);
        setLastNameAr(translated.lastNameAr);
      }
      
      toast.success(language === 'ar' ? 'تمت ترجمة الاسم تلقائياً' : 'Name translated automatically');
    } catch (error) {
      console.error('Error translating name:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleRegisterAndJoin = async () => {
    // Validate names
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(language === 'ar' ? 'الرجاء إدخال الاسم الكامل' : 'Please enter your full name');
      return;
    }

    if (!firstNameAr || !lastNameAr || !firstNameEn || !lastNameEn) {
      toast.error(language === 'ar' ? 'الرجاء الانتظار حتى تكتمل الترجمة' : 'Please wait for translation to complete');
      return;
    }

    try {
      setIsRegistering(true);
      
      // Create user document
      const newUserId = `clinic-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const newUserData = {
        id: newUserId,
        phone: customerPhone,
        firstNameAr,
        lastNameAr,
        firstNameEn,
        lastNameEn,
        role: 'customer',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        registeredVia: 'clinic-link',
        registeredByCoach: coachId,
      };
      
      await setDoc(doc(db, 'users', newUserId), newUserData);
      
      // Set customer info
      setCustomerId(newUserId);
      const fullName = language === 'ar' 
        ? `${firstNameAr} ${lastNameAr}`
        : `${firstNameEn} ${lastNameEn}`;
      setCustomerName(fullName);
      
      toast.success(language === 'ar' ? 'تم التسجيل بنجاح!' : 'Registration successful!');
      
      // Join queue
      await handleJoinQueue(newUserId, fullName, customerPhone);
    } catch (error) {
      console.error('Error registering user:', error);
      toast.error(language === 'ar' ? 'فشل في التسجيل' : 'Registration failed');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleJoinQueue = async (userId?: string, userName?: string, userPhone?: string) => {
    try {
      if (!coachId) return;

      const finalCustomerId = userId || customerId || user?.uid;
      const finalCustomerName = userName || customerName;
      const finalCustomerPhone = userPhone || customerPhone || userData?.phone;

      if (!finalCustomerId || !finalCustomerName) {
        toast.error(language === 'ar' ? 'بيانات غير كاملة' : 'Incomplete data');
        return;
      }

      // Just set state - WaitingRoom component will handle RTDB presence
      setCustomerId(finalCustomerId);
      setCustomerName(finalCustomerName);
      if (finalCustomerPhone) setCustomerPhone(finalCustomerPhone);
      setPhase('queue');
      
      // No need to add to Firestore - RTDB presence will handle it
    } catch (error: any) {
      console.error('Error joining queue:', error);
      if (error.message === 'Coach is offline') {
        setPhase('closed');
        toast.error(language === 'ar' ? 'العيادة مغلقة حالياً' : 'Clinic is currently closed');
      } else if (error.message === 'Customer already in queue') {
        setPhase('queue');
      } else if (error.message === 'Queue is full') {
        toast.error(language === 'ar' ? 'الطابور ممتلئ، حاول لاحقاً' : 'Queue is full, try again later');
      } else {
        toast.error(error.message || (language === 'ar' ? 'فشل في الانضمام' : 'Failed to join queue'));
      }
    }
  };

  const handleCallStarted = (newRoomId: string) => {
    setRoomId(newRoomId);
    setPhase('call');
  };

  const handleCallEnded = (_durationMinutes?: number, outcome?: CallOutcome) => {
    if (outcome && !outcome.connected) {
      // The doctor never joined: back to the waiting room, not "completed" (#47)
      toast.info(language === 'ar'
        ? 'لم يكتمل الاتصال، عدت إلى غرفة الانتظار'
        : 'The call did not connect; you are back in the waiting room');
      setPhase('queue');
      return;
    }
    toast.success(language === 'ar' ? 'انتهت المكالمة بنجاح' : 'Call completed successfully');
    setPhase('info');
  };

  const handleLeaveQueue = () => {
    // Simply go back to info phase - customer is already not in queue
    setPhase('info');
  };

  const getCoachName = () => {
    if (!coachData) return language === 'ar' ? 'الطبيب' : 'Doctor';
    
    return language === 'ar'
      ? `${coachData.firstNameAr || coachData.firstNameEn || 'طبيب'} ${coachData.lastNameAr || coachData.lastNameEn || ''}`.trim()
      : `${coachData.firstNameEn || coachData.firstNameAr || 'Doctor'} ${coachData.lastNameEn || coachData.lastNameAr || ''}`.trim();
  };

  const getStatusBadge = () => {
    if (clinicStatus === 'open') {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          {language === 'ar' ? 'مفتوحة' : 'Open'}
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200">
        <AlertCircle className="h-3 w-3 mr-1" />
        {language === 'ar' ? 'مغلقة' : 'Closed'}
      </Badge>
    );
  };

  const renderTimeRemaining = () => {
    if (!otpExpiry) return null;
    
    const now = new Date();
    const remaining = Math.max(0, Math.floor((otpExpiry.getTime() - now.getTime()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    
    return (
      <p className="text-sm text-muted-foreground text-center">
        {language === 'ar' 
          ? `الرمز صالح لمدة ${minutes}:${seconds.toString().padStart(2, '0')}`
          : `Code expires in ${minutes}:${seconds.toString().padStart(2, '0')}`
        }
      </p>
    );
  };

  // Loading phase
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20 p-4" dir={dir}>
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">
              {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error phase
  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-secondary/20 p-4" dir={dir}>
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {language === 'ar' ? 'خطأ' : 'Error'}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {error}
            </p>
            <Button onClick={() => navigate('/')}>
              {language === 'ar' ? 'العودة للرئيسية' : 'Go Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Closed phase
  if (phase === 'closed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-4" dir={dir}>
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Stethoscope className="h-8 w-8 text-gray-400" />
            </div>
            <CardTitle>{getCoachName()}</CardTitle>
          </CardHeader>
          <CardContent className="text-center py-6">
            <Badge className="bg-red-100 text-red-800 border-red-200 mb-4">
              <AlertCircle className="h-3 w-3 mr-1" />
              {language === 'ar' ? 'العيادة مغلقة' : 'Clinic Closed'}
            </Badge>
            <p className="text-muted-foreground mb-6">
              {language === 'ar' 
                ? 'العيادة غير متاحة حالياً. يرجى المحاولة لاحقاً.'
                : 'The clinic is currently unavailable. Please try again later.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              {language === 'ar' ? 'العودة للرئيسية' : 'Go Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Phone input phase (for non-logged-in users)
  if (phase === 'phone') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20 p-4" dir={dir}>
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            {/* Clinic Info */}
            <div className="w-14 h-14 mx-auto mb-3 bg-primary/10 rounded-full flex items-center justify-center">
              <Stethoscope className="h-7 w-7 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'ar' ? 'عيادة الطبيب' : "Doctor's Clinic"}
            </p>
            <CardTitle className="text-lg mb-1">{getCoachName()}</CardTitle>
            <div className="flex items-center justify-center gap-2 mb-4">
              {getStatusBadge()}
              {queueLength > 0 && (
                <Badge variant="outline" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {queueLength} {language === 'ar' ? 'منتظرين' : 'waiting'}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {language === 'ar' 
                ? 'أدخل رقم جوالك للانضمام'
                : 'Enter your phone to join'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">{language === 'ar' ? 'رقم الجوال' : 'Phone Number'}</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="05XXXXXXXX"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                disabled={isOtpLoading}
                maxLength={10}
                className="h-11 text-base"
                dir="ltr"
              />
            </div>

            <Button 
              className="w-full h-11 bg-[#004250] hover:bg-[#003240]" 
              onClick={handleSendOTP}
              disabled={isOtpLoading || isGoogleLoading || !phoneInput}
            >
              {isOtpLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'إرسال رمز التحقق' : 'Send Verification Code'}
                </>
              )}
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {language === 'ar' ? 'أو' : 'or'}
                </span>
              </div>
            </div>

            {/* Google Sign-in Button */}
            <Button 
              variant="outline"
              className="w-full h-11 bg-white hover:bg-gray-50 border-gray-300"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading || isOtpLoading}
            >
              {isGoogleLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {language === 'ar' ? 'جاري الدخول...' : 'Signing in...'}
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {language === 'ar' ? 'الدخول بـ Google' : 'Continue with Google'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // OTP verification phase
  if (phase === 'otp') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20 p-4" dir={dir}>
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MessageSquare className="h-5 w-5" />
              <CardTitle>{language === 'ar' ? 'التحقق من الرقم' : 'Verify Phone'}</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              {language === 'ar' 
                ? `تم إرسال رمز التحقق إلى ${customerPhone}`
                : `Verification code sent to ${customerPhone}`}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'رمز التحقق' : 'Verification Code'}</Label>
              <OTPInput
                value={otpValue}
                onChange={setOtpValue}
                disabled={isOtpLoading}
                dir={dir}
                autoFocus={true}
                onComplete={() => submitButtonRef.current?.focus()}
                className="justify-center"
              />
            </div>

            {renderTimeRemaining()}

            <Button 
              ref={submitButtonRef}
              className="w-full h-11" 
              onClick={handleVerifyOTP}
              disabled={isOtpLoading || otpValue.length !== 4}
            >
              {isOtpLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {language === 'ar' ? 'جاري التحقق...' : 'Verifying...'}
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'تحقق' : 'Verify'}
                </>
              )}
            </Button>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setPhase('phone');
                  setOtpValue('');
                  setOtpExpiry(null);
                }}
                disabled={isOtpLoading}
              >
                <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2 rotate-180' : 'mr-2'}`} />
                {language === 'ar' ? 'تغيير الرقم' : 'Change Phone'}
              </Button>

              <Button 
                variant="outline"
                className="flex-1"
                onClick={handleResendOTP}
                disabled={isResendLoading || resendCooldown > 0}
              >
                {isResendLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {resendCooldown > 0 
                  ? `${language === 'ar' ? 'إعادة الإرسال' : 'Resend'} (${resendCooldown}s)`
                  : language === 'ar' ? 'إعادة الإرسال' : 'Resend Code'
                }
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Details phase - name input with AI translation
  if (phase === 'details') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20 p-4" dir={dir}>
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <CardTitle>{language === 'ar' ? 'إكمال التسجيل' : 'Complete Registration'}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {language === 'ar' 
                  ? `تم التحقق من رقم الجوال: ${customerPhone}`
                  : `Phone verified: ${customerPhone}`}
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName">
                    {language === 'ar' ? 'الاسم الأول' : 'First Name'}
                  </Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={handleNameBlur}
                    placeholder={language === 'ar' ? 'أدخل اسمك' : 'Enter name'}
                    disabled={isRegistering}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">
                    {language === 'ar' ? 'اسم العائلة' : 'Last Name'}
                  </Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={handleNameBlur}
                    placeholder={language === 'ar' ? 'اسم العائلة' : 'Last name'}
                    disabled={isRegistering}
                    className="h-10"
                  />
                </div>
              </div>

              {isTranslating && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  {language === 'ar' ? 'جاري الترجمة التلقائية...' : 'Auto-translating...'}
                </div>
              )}

              {(firstNameAr || firstNameEn) && !isTranslating && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <Sparkles className="h-4 w-4" />
                    {language === 'ar' ? 'الترجمة التلقائية:' : 'Auto-translated:'}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">{language === 'ar' ? 'بالعربية:' : 'Arabic:'}</span>
                      <p className="font-medium" dir="rtl">{firstNameAr} {lastNameAr}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{language === 'ar' ? 'بالإنجليزية:' : 'English:'}</span>
                      <p className="font-medium" dir="ltr">{firstNameEn} {lastNameEn}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button 
              className="w-full h-11 bg-[#004250] hover:bg-[#003240]" 
              onClick={handleRegisterAndJoin}
              disabled={isRegistering || isTranslating || !firstName || !lastName}
            >
              {isRegistering ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {language === 'ar' ? 'جاري التسجيل...' : 'Registering...'}
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'التسجيل والانضمام' : 'Register & Join'}
                </>
              )}
            </Button>

            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setPhase('phone')}
              disabled={isRegistering}
            >
              <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2 rotate-180' : 'mr-2'}`} />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Queue phase
  if (phase === 'queue' && coachId) {
    return (
      <WaitingRoom
        coachId={coachId}
        coachName={getCoachName()}
        coachAvatar={coachData?.profilePicture}
        customerName={customerName}
        customerPhone={customerPhone}
        onCallStarted={handleCallStarted}
        onLeaveQueue={handleLeaveQueue}
        publicCustomerInfo={{
          customerId,
          customerName
        }}
      />
    );
  }

  // Call phase
  if (phase === 'call') {
    return (
      <div className="fixed inset-0 bg-black z-50">
        <FloatingVideoCall 
          roomId={roomId}
          userRole="customer"
          userName={customerName}
          otherParticipantName={getCoachName()}
          onCallEnded={handleCallEnded}
          open={true}
          forceFullscreen={true}
          attempt={coachId && customerId ? { coachId, customerId } : undefined}
        />
      </div>
    );
  }

  return null;
}
